import { useState, useEffect } from 'react';
import { Card, Tooltip } from '@nike/eds';
import { Delete, Plus } from '@nike/nike-design-system-icons';
import { IconButton } from './IconButton';
import { TargetMenu } from './TargetMenu';
import { RankingRuleActionType } from 'types/ranking';
import { RANKING_RULE_TARGET_TYPES } from 'constants/index';
import DraggableContainer from './DraggableContainer';

export default function RankingRuleBoostsOrBuries({
  data,
  type,
  handleItemRemove,
  handleItemDragDrop,
  handleRemoveAll,
}: {
  data: RankingRuleTargetBoostItem[] | RankingRuleTargetBuryItem[];
  type: RankingRuleActionType;
  handleItemRemove: (idx: number) => void;
  handleItemDragDrop: (itemIdx: number, targetIdx: number) => void;
  handleRemoveAll: VoidFunction;
}) {
  const [targetMenuOpen, setTargetMenuOpen] = useState(false);
  return (
    <div>
      <div>
        <div className='tw-flex tw-items-center tw-gap-1 tw-group'>
          <h3 className='tw-my-2 tw-text-lg tw-font-bold'>
            {type === RankingRuleActionType.Boost ? 'Boosts' : 'Buries'}
          </h3>
          <TargetMenu
            isOpen={targetMenuOpen}
            actionType={type}
            closeMenu={() => setTargetMenuOpen(false)}
          >
            <IconButton
              className='tw-invisible group-hover:tw-visible'
              icon={<Plus width={20} height={20} />}
              title='Add'
              label='Add'
              onClick={() => setTargetMenuOpen(true)}
            />
          </TargetMenu>
          <Tooltip
            bodySlot={<p className='tw-text-sm'>Remove All</p>}
            placement={'top'}
            isDark
          >
            <IconButton
              className='tw-invisible group-hover:tw-visible'
              label='Delete'
              icon={<Delete width={20} height={20} />}
              onClick={() => handleRemoveAll()}
            />
          </Tooltip>
        </div>
        {data?.length > 0 ? (
          <>
            <p className='tw-text-sm tw-text-gray-500'>
              You can drag and drop to reorder.
            </p>
            <p className='tw-text-sm tw-text-gray-500 tw-pb-2'>
              {type === RankingRuleActionType.Boost
                ? 'Style Color, Style Code and PBID will always be prioritized before other types.'
                : ''}
            </p>
          </>
        ) : null}
      </div>

      {data?.length === 0 && (
        <p className='tw-text-sm tw-text-gray-500 tw-pb-2'>
          {`There is no ${type} yet`}
        </p>
      )}
      {data?.length > 0 && type === RankingRuleActionType.Boost && (
        <BoostsContainer
          data={data}
          handleItemDragDrop={handleItemDragDrop}
          handleItemRemove={handleItemRemove}
        />
      )}
      {data?.length > 0 && type === RankingRuleActionType.Bury && (
        <BuriesContainer
          data={data}
          handleItemDragDrop={handleItemDragDrop}
          handleItemRemove={handleItemRemove}
        />
      )}
    </div>
  );
}

const BoostsContainer = (props: {
  data: RankingRuleTargetBoostItem[] | RankingRuleTargetBuryItem[];
  handleItemRemove: (idx: number) => void;
  handleItemDragDrop: (itemIdx: number, targetIdx: number) => void;
}) => {
  const { data, handleItemDragDrop, handleItemRemove } = props;
  const [styleColorCodeItems, setStyleColorCodeItems] = useState<
    RankingRuleTargetBoostItem[] | RankingRuleTargetBuryItem[]
  >([]);
  const [otherItems, setOtherItems] = useState<
    RankingRuleTargetBoostItem[] | RankingRuleTargetBuryItem[]
  >([]);

  useEffect(() => {
    const scItems = data.filter(
      (item) =>
        item.type === RANKING_RULE_TARGET_TYPES.STYLE_COLOR ||
        item.type === RANKING_RULE_TARGET_TYPES.STYLE_CODE ||
        item.type === RANKING_RULE_TARGET_TYPES.PBID,
    );
    const otherItems = data.filter(
      (item) =>
        item.type !== RANKING_RULE_TARGET_TYPES.STYLE_COLOR &&
        item.type !== RANKING_RULE_TARGET_TYPES.STYLE_CODE &&
        item.type !== RANKING_RULE_TARGET_TYPES.PBID,
    );
    setStyleColorCodeItems(scItems);
    setOtherItems(otherItems);
  }, [data]);
  return (
    <Card className='tw-flex tw-items-center tw-justify-start tw-gap-2 tw-flex-wrap'>
      {styleColorCodeItems.length > 0 && (
        <DraggableContainer
          items={styleColorCodeItems}
          type={RankingRuleActionType.Boost}
          handleItemReorder={(itemIdx, targetIdx) => {
            handleItemDragDrop(itemIdx, targetIdx);
          }}
          handleItemRemove={(itemToDel) => {
            const originalIdx = data.findIndex(
              (item) => JSON.stringify(item) === JSON.stringify(itemToDel),
            );
            handleItemRemove(originalIdx);
          }}
        />
      )}
      {otherItems.length > 0 && (
        <DraggableContainer
          items={otherItems}
          type={RankingRuleActionType.Boost}
          handleItemReorder={(itemIdx, targetIdx) => {
            const offset = styleColorCodeItems.length;
            handleItemDragDrop(itemIdx + offset, targetIdx + offset);
          }}
          handleItemRemove={(itemToDel) => {
            const originalIdx = data.findIndex(
              (item) => JSON.stringify(item) === JSON.stringify(itemToDel),
            );
            handleItemRemove(originalIdx);
          }}
        />
      )}
    </Card>
  );
};

const BuriesContainer = (props: {
  data: RankingRuleTargetBoostItem[] | RankingRuleTargetBuryItem[];
  handleItemRemove: (idx: number) => void;
  handleItemDragDrop: (itemIdx: number, targetIdx: number) => void;
}) => {
  const { data, handleItemDragDrop, handleItemRemove } = props;

  return (
    <Card className='tw-flex tw-items-center tw-justify-start tw-gap-2 tw-flex-wrap'>
      {data.length > 0 && (
        <DraggableContainer
          items={data}
          type={RankingRuleActionType.Bury}
          handleItemReorder={(itemIdx, targetIdx) => {
            handleItemDragDrop(itemIdx, targetIdx);
          }}
          handleItemRemove={(itemToDel) => {
            const originalIdx = data.findIndex(
              (item) => JSON.stringify(item) === JSON.stringify(itemToDel),
            );
            handleItemRemove(originalIdx);
          }}
        />
      )}
    </Card>
  );
};
